<template>
    <div class="rater-table overflow-x-auto" :class="{ 'with-actions': remindable }" role="table" :aria-label="t('reflectionsAdmin.raterTable.tableLabel')">
        <div class="rater-grid header" role="row">
            <span role="columnheader">{{ t("reflectionsAdmin.raterTable.rater") }}</span>
            <span role="columnheader">{{ t("reflectionsAdmin.raterTable.relationship") }}</span>
            <span role="columnheader">{{ t("reflectionsAdmin.raterTable.status") }}</span>
            <span v-if="remindable" role="columnheader" class="sr-only">{{ t("reflectionsAdmin.raterTable.actions") }}</span>
        </div>
        <div v-for="row in rows" :key="row.invite_id" class="rater-grid row" role="row">
            <div class="identity flex items-center gap-2.5 min-w-0" role="cell">
                <BaseAvatar size="xs" theme="identity" :initials="row.rater_initials" :identity-key="row.invite_id" />
                <span class="flex flex-col min-w-0">
                    <span class="body-small text-ink break-all">{{ raterName(row) }}</span>
                    <span class="details text-ink-muted truncate">{{ t("reflectionsAdmin.raterTable.rating", { subject: subjectName(row) }) }}</span>
                </span>
            </div>
            <span class="details text-ink-muted truncate" role="cell">{{ relationshipLabel(row.relationship) }}</span>
            <span role="cell"
                ><StatusChip :state="chipState(row.state)" size="xs">{{ stateLabel(row.state) }}</StatusChip></span
            >
            <span v-if="remindable" class="action justify-self-end" role="cell">
                <BaseTooltip>
                    <template #trigger>
                        <span
                            class="inline-flex"
                            :tabindex="canRemind(row) ? -1 : 0"
                            :role="canRemind(row) ? undefined : 'button'"
                            :aria-disabled="canRemind(row) ? undefined : 'true'"
                            :aria-label="canRemind(row) ? undefined : t('reflectionsAdmin.raterTable.remind', { name: raterName(row) })"
                            :aria-description="canRemind(row) ? undefined : remindTooltip(row)">
                            <BaseButton
                                theme="tertiary"
                                size="sm"
                                icon-only
                                :disabled="!canRemind(row)"
                                :aria-label="t('reflectionsAdmin.raterTable.remind', { name: raterName(row) })"
                                @click="emit('remind', row)">
                                <i class="bi bi-send" aria-hidden="true"></i>
                            </BaseButton>
                        </span>
                    </template>
                    <template #content>{{ remindTooltip(row) }}</template>
                </BaseTooltip>
            </span>
        </div>
    </div>
</template>

<script setup lang="ts">
import type { AdminRaterRow, AdminRaterState, ReflectionResponseRelationship } from "~vue/api/generated/reflections-admin";
import BaseAvatar from "~vue/components/BaseAvatar.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseTooltip from "~vue/components/BaseTooltip.vue";
import StatusChip, { type StatusChipState } from "~vue/components/StatusChip.vue";
import { useI18n } from "vue-i18n";

interface Props {
    rows: AdminRaterRow[];
    // Closed campaigns can't collect any more, so an open invite reads as "No response".
    campaignClosed?: boolean;
    /** Adds a per-row "send reminder" action; only active campaigns offer it. */
    remindable?: boolean;
}

const { rows, campaignClosed = false, remindable = false } = defineProps<Props>();
const emit = defineEmits<{ remind: [row: AdminRaterRow] }>();
const { t } = useI18n();

const relationshipLabels: Record<ReflectionResponseRelationship, () => string> = {
    peer: () => t("reflectionsAdmin.raterTable.relationshipLabel.peer"),
    manager: () => t("reflectionsAdmin.raterTable.relationshipLabel.manager"),
    skip_manager: () => t("reflectionsAdmin.raterTable.relationshipLabel.skipManager"),
    direct: () => t("reflectionsAdmin.raterTable.relationshipLabel.direct"),
    other: () => t("reflectionsAdmin.raterTable.relationshipLabel.other"),
    self: () => t("reflectionsAdmin.raterTable.relationshipLabel.self"),
};

function relationshipLabel(relationship: ReflectionResponseRelationship | null | undefined): string {
    return relationship ? relationshipLabels[relationship]() : t("reflectionsAdmin.raterTable.noRelationship");
}

function raterName(row: AdminRaterRow): string {
    return row.rater_is_former_member ? t("reflectionsAdmin.raterTable.formerMember") : row.rater_name;
}

function subjectName(row: AdminRaterRow): string {
    return row.subject_is_former_member ? t("reflectionsAdmin.raterTable.formerMember") : row.subject_name;
}

// A pending invite is the only one a reminder can still move; an internal rater
// who has left the org has no work address here to send it to, and nobody is
// chased on behalf of a subject who has left (the API refuses both).
function canRemind(row: AdminRaterRow): boolean {
    return row.state === "pending" && !row.rater_is_former_member && !row.subject_is_former_member;
}

function remindTooltip(row: AdminRaterRow): string {
    if (row.rater_is_former_member) {
        return t("reflectionsAdmin.raterTable.remindFormerMember");
    }
    if (row.subject_is_former_member) {
        return t("reflectionsAdmin.raterTable.remindSubjectFormerMember");
    }
    if (row.state === "responded") {
        return t("reflectionsAdmin.raterTable.remindAlreadyResponded");
    }
    if (row.state === "expired") {
        return t("reflectionsAdmin.raterTable.remindExpired");
    }
    return t("reflectionsAdmin.raterTable.remindHint");
}

function chipState(state: AdminRaterState): StatusChipState {
    return state === "responded" ? "done" : "neutral";
}

// Closing a campaign expires its open invites, so on a closed campaign both
// pending and expired mean the rater never answered.
function stateLabel(state: AdminRaterState): string {
    if (state === "responded") {
        return t("reflectionsAdmin.raterTable.state.responded");
    }
    if (campaignClosed) {
        return t("reflectionsAdmin.raterTable.state.noResponse");
    }
    return state === "expired" ? t("reflectionsAdmin.raterTable.state.expired") : t("reflectionsAdmin.raterTable.state.pending");
}
</script>

<style scoped>
@reference "../../../../css/tailwind.css";

@layer components {
    .rater-table {
        @apply grid;
        grid-template-columns: minmax(160px, 1fr) max-content max-content;
    }

    .rater-table.with-actions {
        grid-template-columns: minmax(160px, 1fr) max-content max-content max-content;
    }

    .rater-grid {
        @apply grid grid-cols-subgrid col-span-full items-center gap-3 px-2;
    }

    .rater-grid.header {
        @apply scaffold-details-small text-ink-muted pb-2 border-b border-border;
    }

    .rater-grid.row {
        @apply py-3 border-b border-border rounded-md transition-colors hover:bg-surface-hover;
    }

    /* Narrow screens: see AdminParticipantTable — rows reflow instead of scrolling. */
    @media (width < theme(--breakpoint-md)) {
        .rater-table,
        .rater-table.with-actions {
            grid-template-columns: 1fr;
        }

        .rater-grid.header {
            @apply sr-only;
        }

        .rater-grid.row {
            @apply flex flex-wrap gap-2;
        }

        .rater-grid.row > .identity {
            @apply basis-full;
        }

        .rater-grid.row > .action {
            @apply ml-auto;
        }
    }
}
</style>
